import {Translator} from "../translation/translation-service";
import {getCountryService} from "../country/country-service";

export class Product {
  public brand: string;
  public country: string;
  public maintenanceGroup: string;
  public variants: Variant[];
  public webCategory: string;
  public customerGroup: string;
  public descriptions: Description[];

  constructor(values: any) {
    this.brand = values.brand;
    this.country = values.country;
    this.maintenanceGroup = values.maintenance_group;
    let variants: Variant[] = [];
    if (values.variants) {
      for (let valueVariant of values.variants) {
        if (valueVariant) {
          variants.push(new Variant(valueVariant))
        }
      }
    }
    this.variants = variants;

    let descriptions: Description[] = [];
    if (values.descriptions) {

      for (let description of values.descriptions) {
        descriptions.push(new Description(description))
      }
    }
    this.descriptions = descriptions;

    this.webCategory = values.web_category;
    this.customerGroup = values.customer_group;
  }

  getVariant(productId: string, variantId: string): Variant {
    return this.variants.find(variant => {
      return variant.productId === productId && variant.id === variantId;
    });
  }

  getLabel(translator: Translator): string {
    if (this.descriptions?.length > 0) {
      const countryCode = getCountryService().getCountryCodeFromLocation().toUpperCase();
      const language = getCountryService().getLanguageForCountry(countryCode);
      const fromCountryCode = this.descriptions.find(d => d.language.toUpperCase() === language.toUpperCase());
      const fallback = this.descriptions.find(d => d.language.toUpperCase() === 'EN');
      const german = this.descriptions.find(d => d.language.toUpperCase() === 'DE');

      if (fromCountryCode) return fromCountryCode.description
      return ['DE', 'AT', 'CH'].includes(countryCode) ? german?.description : fallback?.description;
    }

    let filterKey = this.webCategory.trim().toLowerCase();
    filterKey = filterKey.replace(new RegExp(' ', 'g'), '_');
    if (this.customerGroup.toLowerCase() === 'male') {
      return translator.translate(`webcat_haka_singular_${filterKey}`);
    } else {
      return translator.translate(`webcat_singular_${filterKey}`);
    }
  }
}

export class Description {
  public description: string;
  public language: string;

  constructor(values: any) {
    this.description = values.description;
    this.language = values.language;
  }
}

export class Size {
  public sizeValue: string;
  public sizeName: string;
  public barCode: string;

  constructor(values: any) {
    this.sizeValue = values.size_value;
    this.sizeName = values.size_name;
    this.barCode = values.bar_code;
  }
}

export type ImageType = 'CUTOUT' | 'OUTFIT_VIDEO' | 'OUTFIT_IMAGE'

export class ProductImage {
  public angle: string;
  public key: string;
  public hasThumbnail: boolean;
  public type: ImageType;
  public position: number;

  constructor(values: any) {
    this.angle = values.angle;
    this.key = values.key;
    this.hasThumbnail = values.hasThumbnail ? values.hasThumbnail : values.has_thumbnail ? values.has_thumbnail : false;
    this.type = values.type ? values.type : 'CUTOUT';
    this.position = values.position;
  }
}

export class Variant {
  public id: string;
  public productId: string;
  public currency: string;
  public images: ProductImage[];
  public currentPrice: number;
  public originalPrice: number;
  public pantoneColor: string;
  public redPriceChange: boolean;
  public comingSoon: boolean;
  public newIn: boolean;
  public sizes: Size[];
  public basicColor: string;
  public colorGroup: string;
  public sale: boolean;
  public red: number;
  public green: number;
  public blue: number;

  private colorName: string;
  private publishDate: Date;


  constructor(values: any) {
    this.basicColor = values.basic_color ? values.basic_color : values.basicColor;
    this.blue = values.blue;
    this.colorGroup = values.color_group ? values.color_group : values.colorGroup;
    this.colorName = values.color_name ? values.color_name : values.colorName;
    this.currency = values.currency;
    this.currentPrice = values.current_price ? values.current_price : values.currentPrice;
    this.originalPrice = values.original_price ? values.original_price : values.originalPrice;

    this.green = values.green;
    this.comingSoon = values.coming_soon ? values.coming_soon : values.comingSoon;
    this.newIn = values.new_in ? values.new_in : values.newIn;
    this.sale = values.sale ? values.sale : false;

    this.images = [];
    for (let image of values.images) {
      this.images.push(new ProductImage(image))
    }

    this.pantoneColor = values.pantone_color ? values.pantone_color : values.pantoneColor;
    this.publishDate = values.publish_date ? values.publish_date : values.publishDate;
    this.red = values.red;

    if (this.originalPrice <= this.currentPrice) {
      this.redPriceChange = false
    } else {
      this.redPriceChange = values.red_price_change ? values.red_price_change : values.redPriceChange;
    }

    this.sizes = [];
    for (let size of values.sizes) {
      this.sizes.push(new Size(size))
    }

    this.id = values.id;
    this.productId = values.product_id ? values.product_id : values.productId;
  }

  getCutout() {
    let imageKey = null;

    //search first front-cutout
    for (const img of this.images) {
      if (img.type === "CUTOUT" && img.angle === "FRONT") {
        imageKey = img.key;
        break;
      }
    }
    //search first cutout
    if (!imageKey) {
      for (const cutout of this.images) {
        if (cutout.type === "CUTOUT") {
          imageKey = cutout.key;
          break;
        }
      }
    }

    if (!imageKey) {
      console.warn("no cutout found for product " + this.productId + " / " + this.id);
      return;
    }

    return imageKey;
  }

  getCurrentPrice(languageCode: string, countryCode: string): string {
    if (this.comingSoon) {
      return "COMING SOON"
    }
    const productPrice = this.currentPrice || 0;
    return `${productPrice.toLocaleString(`${languageCode}-${countryCode.toUpperCase()}`, {style:"currency", currency: this.currency, currencyDisplay:'code'})}`;
  }
}
